import { useAuth } from "@/features/auth";
import { Role, ROLES, UserPermissions } from "@/features/auth/types";
import { useProfileSwitcher } from "@/hooks/useProfileSwitcher";

export function usePermissao(): UserPermissions {
  const { state: { user } } = useAuth();
  const { getCurrentProfile } = useProfileSwitcher();

  // Obter o perfil atual (real ou simulado)
  const currentProfile = getCurrentProfile;

  // Superadmin só recebe todas as permissões quando NÃO está simulando outro perfil.
  // Ao simular, as permissões abaixo passam a refletir o perfil selecionado.
  if (user?.role === ROLES.SUPER_ADMIN && currentProfile === ROLES.SUPER_ADMIN) {
    return {
      // Permissões de papéis
      isSuperAdmin: true,
      isAdmin: true,
      isAdministradorGeral: true,
      isSecretaria: true,
      isSupervisor: true,
      isRelacionador: true,
      isLiderVendas: true,
      isPosVenda: true,
      isConsultor: true,
      isFinanceiro: true,

      // Permissões granulares - Contratos
      podeCriarContrato: true,
      podeEditarContrato: true,
      podeVisualizarContratos: true,
      podeFinalizarVenda: true,
      podeRepassarCliente: true,
      podeReceberCliente: true,
      podeTransferirContrato: true,
      podeReceberContrato: true,
      podeAprovarContrato: true,
      podeRejeitarContrato: true,
      podeFinalizarContrato: true,
      podeExcluirContrato: true,
      podeVisualizarContratosTime: true,
      podeConfirmarRealizacaoAgenda: true,

      // Permissões granulares - Clientes
      podeCriarCliente: true,
      podeEditarCliente: true,
      podeVisualizarClientes: true,
      podeVisualizarTodosClientes: true,
      podeVisualizarClientesTime: true,

      // Permissões granulares - Documentos
      podeGerenciarDocumentos: true,
      podeVisualizarDocumentos: true,

      // Permissões granulares - Agenda
      podeGerenciarAgenda: true,
      podeVisualizarAgenda: true,

      // Permissões granulares - Sistema
      podeConvidarUsuarios: true,
      podeAcessarRelatorios: true,
      podeEditarConfiguracoes: true,
      podeGerenciarTimes: true,
      podeGerenciarGrupos: true,
      podeGerenciarBens: true,
      podeGerenciarComissoes: true,

      verificarPermissao: () => true
    };
  }

  // Flags de papéis baseadas no perfil atual (real ou simulado)
  // Associado administra a própria operação de vendas, mas NÃO herda
  // setores da matriz (pós-venda / secretaria). Representante administra
  // a estrutura completa da própria representação (isolamento via RLS).
  const isRepresentante = currentProfile === ROLES.REPRESENTANTE;
  const isAssociado = currentProfile === ROLES.ASSOCIADO;
  const isAdministradorGeral = isAssociado || isRepresentante;
  // Secretaria / Pós-venda: só o próprio cargo (ou Representante gerindo o tenant).
  // Associado explicitamente NÃO herda esses setores.
  const isSecretaria = currentProfile === ROLES.SECRETARIA || isRepresentante;
  const isSupervisor = currentProfile === ROLES.SUPERVISOR || isAdministradorGeral;
  const isRelacionador = currentProfile === ROLES.RELACIONADOR || isAdministradorGeral;
  const isLiderVendas = currentProfile === ROLES.LIDER_VENDAS || isAdministradorGeral;
  const isPosVenda = currentProfile === ROLES.POS_VENDA;
  const isConsultor = currentProfile === ROLES.USER || isAdministradorGeral;
  const isFinanceiro = isAdministradorGeral;
  const isSuperAdmin = false;

  // Permissões baseadas na nova hierarquia

  // Contratos
  // Relacionadores (SDR) NÃO podem criar contratos, apenas clientes e agendamentos
  const podeCriarContrato = isAdministradorGeral || isSupervisor || isLiderVendas;
  const podeEditarContrato = isAdministradorGeral || isSupervisor || isRelacionador || isLiderVendas;
  const podeVisualizarContratos = isAdministradorGeral || isSecretaria || isSupervisor || isRelacionador || isLiderVendas || isPosVenda;
  const podeFinalizarVenda = isAdministradorGeral || isLiderVendas || isSupervisor; // Relacionador (SDR) NÃO finaliza vendas
  const podeRepassarCliente = isAdministradorGeral || isRelacionador; // SDR (Relacionador) repassa para o Closer
  const podeReceberCliente = isAdministradorGeral || isLiderVendas; // Closer (Líder de Vendas) recebe do SDR
  const podeTransferirContrato = isAdministradorGeral || isSupervisor; // Apenas Supervisores ou Admin transferem
  const podeReceberContrato = isAdministradorGeral || isLiderVendas; // Closer recebe o contrato
  const podeAprovarContrato = isAdministradorGeral || isSecretaria || isSupervisor;
  const podeRejeitarContrato = isAdministradorGeral || isSecretaria || isSupervisor;
  const podeFinalizarContrato = isAdministradorGeral || isRelacionador || isSupervisor;
  const podeExcluirContrato = isAdministradorGeral || isSupervisor || isSecretaria || isSuperAdmin;
  const podeVisualizarContratosTime = isAdministradorGeral || isSupervisor;

  // Clientes
  const podeCriarCliente = isAdministradorGeral || isSupervisor || isRelacionador || isLiderVendas;
  const podeEditarCliente = isAdministradorGeral || isSupervisor || isRelacionador || isLiderVendas;
  const podeVisualizarClientes = isAdministradorGeral || isSecretaria || isSupervisor || isRelacionador || isLiderVendas;
  const podeVisualizarTodosClientes = isAdministradorGeral || isSecretaria; // Apenas Admin e Secretária veem todos
  const podeVisualizarClientesTime = isAdministradorGeral || isSupervisor; // Supervisor vê seu time

  // Documentos
  const podeGerenciarDocumentos = isAdministradorGeral || isSupervisor || isSecretaria;
  const podeVisualizarDocumentos = isAdministradorGeral || isSecretaria || isSupervisor || isRelacionador || isLiderVendas;

  // Agenda
  // Líder de Vendas cria agendamentos, Relacionador confirma realização
  const podeGerenciarAgenda = isAdministradorGeral || isSupervisor || isSecretaria || isRelacionador || isLiderVendas;
  const podeConfirmarRealizacaoAgenda = isAdministradorGeral || isSupervisor || isSecretaria || isRelacionador || isLiderVendas;
  const podeVisualizarAgenda = isAdministradorGeral || isSecretaria || isSupervisor || isRelacionador || isLiderVendas;

  // Sistema
  const podeConvidarUsuarios = isAdministradorGeral || isSupervisor;
  const podeAcessarRelatorios = isAdministradorGeral || isSupervisor;
  const podeEditarConfiguracoes = isAdministradorGeral;
  const podeGerenciarTimes = isAdministradorGeral || isSupervisor;
  const podeGerenciarGrupos = currentProfile === ROLES.SECRETARIA || isSuperAdmin;
  const podeGerenciarBens = currentProfile === ROLES.SECRETARIA || isSuperAdmin;
  const podeGerenciarComissoes = currentProfile === ROLES.SECRETARIA || isSuperAdmin;

  const verificarPermissao = (requiredPermissions: Role[]): boolean => {
    if (!user) return false;

    // Se não há permissões específicas definidas, permite acesso
    if (requiredPermissions.length === 0) {
      return true;
    }

    // Superadmin só ignora a checagem quando não está simulando outro perfil
    if (user.role === ROLES.SUPER_ADMIN && currentProfile === ROLES.SUPER_ADMIN) {
      return true;
    }

    return requiredPermissions.some(permission => {
      switch (permission) {
        case ROLES.ASSOCIADO:
          return isAdministradorGeral;
        case ROLES.REPRESENTANTE:
          return isRepresentante;
        case ROLES.SECRETARIA:
          return isSecretaria;
        case ROLES.SUPERVISOR:
          return isSupervisor;
        case ROLES.RELACIONADOR:
          return isRelacionador;
        case ROLES.LIDER_VENDAS:
          return isLiderVendas;
        case ROLES.POS_VENDA:
          return isPosVenda;
        case ROLES.SUPER_ADMIN:
          return isSuperAdmin;
        default:
          return false;
      }
    });
  };

  return {
    // Permissões de papéis
    isSuperAdmin,
    isAdmin: isAdministradorGeral,
    isAdministradorGeral,
    isSecretaria,
    isSupervisor,
    isRelacionador,
    isLiderVendas,
    isPosVenda,
    isConsultor,
    isFinanceiro,

    // Permissões granulares - Contratos
    podeCriarContrato,
    podeEditarContrato,
    podeVisualizarContratos,
    podeFinalizarVenda,
    podeRepassarCliente,
    podeReceberCliente,
    podeTransferirContrato,
    podeReceberContrato,
    podeAprovarContrato,
    podeRejeitarContrato,
    podeFinalizarContrato,
    podeExcluirContrato,
    podeVisualizarContratosTime,

    // Permissões granulares - Clientes
    podeCriarCliente,
    podeEditarCliente,
    podeVisualizarClientes,
    podeVisualizarTodosClientes,
    podeVisualizarClientesTime,

    // Permissões granulares - Documentos
    podeGerenciarDocumentos,
    podeVisualizarDocumentos,

    // Permissões granulares - Agenda
    podeGerenciarAgenda,
    podeConfirmarRealizacaoAgenda,
    podeVisualizarAgenda,

    // Permissões granulares - Sistema
    podeConvidarUsuarios,
    podeAcessarRelatorios,
    podeEditarConfiguracoes,
    podeGerenciarTimes,
    podeGerenciarGrupos,
    podeGerenciarBens,
    podeGerenciarComissoes,

    verificarPermissao
  };
} 